import { useState, lazy, Suspense } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, MessageCircle, MoreHorizontal, Pencil, Send, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";

import { Linkify } from "@/components/Linkify";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  listPostComments,
  createComment,
  updatePostComment,
  deletePostComment,
} from "@/lib/feed.functions";

const GifPicker = lazy(() =>
  import("@/components/ui/gif-picker").then((m) => ({ default: m.GifPicker })),
);

export function CommentsPanel({ postId }: { postId: string }) {
  const qc = useQueryClient();
  const { user } = useCurrentUser();
  const listFn = useServerFn(listPostComments);
  const addFn = useServerFn(createComment);
  const updateFn = useServerFn(updatePostComment);
  const deleteFn = useServerFn(deletePostComment);
  const [text, setText] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["post-comments", postId],
    queryFn: () => listFn({ data: { postId } }),
    staleTime: 30_000,
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["post-comments", postId] });
    qc.invalidateQueries({ queryKey: ["following-feed"] });
  };
  const add = useMutation({
    mutationFn: (vars: { body?: string; mediaUrl?: string } = {}) =>
      addFn({
        data: {
          postId,
          body: vars.body ?? text,
          mediaUrl: vars.mediaUrl,
          mediaType: vars.mediaUrl ? "gif" : undefined,
        },
      }),
    onSuccess: () => {
      setText("");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const editMut = useMutation({
    mutationFn: (vars: { id: string; body: string }) => updateFn({ data: vars }),
    onSuccess: () => invalidate(),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => invalidate(),
  });
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {isLoading ? (
          <div className="space-y-3">
            <div className="h-12 animate-pulse rounded-lg bg-surface/60" />
            <div className="h-12 animate-pulse rounded-lg bg-surface/60" />
          </div>
        ) : (data ?? []).length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-sm text-muted-foreground">
            <MessageCircle className="mb-3 h-6 w-6 opacity-40" strokeWidth={1.5} />
            Seja o primeiro a comentar.
          </div>
        ) : (
          <ul className="space-y-6">
            {(data ?? []).map((c) => (
              <CommentRow
                key={c.id}
                comment={c}
                isOwner={user?.id === c.user_id}
                onEdit={(body) => editMut.mutate({ id: c.id, body })}
                onDelete={() => delMut.mutate(c.id)}
                pending={editMut.isPending || delMut.isPending}
              />
            ))}
          </ul>
        )}
      </div>
      {!user ? (
        <div className="border-t border-border/60 px-6 py-4 text-center text-[13px] text-muted-foreground">
          Entre para curtir e comentar.
        </div>
      ) : (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) add.mutate({});
        }}
        className="flex items-center gap-2 border-t border-border/60 px-6 py-4"
      >
        <Suspense fallback={<div className="h-9 w-9" />}><GifPicker onPick={(g) => add.mutate({ mediaUrl: g.url })} /></Suspense>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Escreva um comentário…"
          className="h-10 flex-1 rounded-full border border-border/60 bg-surface/60 px-4 text-sm placeholder:text-muted-foreground/70 focus:border-foreground/30 focus:outline-none"
          maxLength={1000}
        />
        <button
          type="submit"
          disabled={!text.trim() || add.isPending}
          className="grid h-10 w-10 place-items-center rounded-full bg-primary text-primary-foreground transition-opacity disabled:opacity-40"
          aria-label="Enviar"
        >
          {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
      )}
    </div>
  );
}

type CommentRowProps = {
  comment: {
    id: string;
    body: string;
    created_at: string;
    edited_at?: string | null;
    user_id: string;
    author: {
      username: string;
      display_name: string | null;
      avatar_url: string | null;
    } | null;
  };
  isOwner: boolean;
  onEdit: (body: string) => void;
  onDelete: () => void;
  pending: boolean;
};

function CommentRow({ comment, isOwner, onEdit, onDelete, pending }: CommentRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <li className="flex items-start gap-3">
      {comment.author?.avatar_url ? (
        <img loading="lazy" decoding="async" src={comment.author.avatar_url} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
      ) : (
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-surface text-[10px] font-bold uppercase text-muted-foreground">
          {(comment.author?.display_name ?? comment.author?.username ?? "?").slice(0, 1)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-baseline gap-2">
          <span className="text-[12px] font-semibold tracking-tight">
            {comment.author?.display_name ?? comment.author?.username ?? "—"}
          </span>
          <span className="text-[10px] text-muted-foreground/70">
            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: ptBR })}
            {comment.edited_at ? " · editado" : ""}
          </span>
          {isOwner && !editing && (
            <Popover open={menuOpen} onOpenChange={setMenuOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="ml-auto rounded-full p-1 text-muted-foreground hover:bg-surface"
                  aria-label="Opções"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-36 p-1">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-surface"
                  onClick={() => {
                    setEditing(true);
                    setMenuOpen(false);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </button>
                <button
                  type="button"
                  disabled={pending}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-destructive hover:bg-surface disabled:opacity-50"
                  onClick={() => {
                    if (confirm("Excluir este comentário?")) {
                      onDelete();
                      setMenuOpen(false);
                    }
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Excluir
                </button>
              </PopoverContent>
            </Popover>
          )}
        </div>
        {editing ? (
          <div className="space-y-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              maxLength={1000}
              className="w-full rounded-lg border border-border/60 bg-surface/60 px-3 py-2 text-sm focus:border-foreground/30 focus:outline-none"
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!draft.trim() || pending}
                onClick={() => {
                  onEdit(draft.trim());
                  setEditing(false);
                }}
                className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
              >
                Salvar
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(comment.body);
                  setEditing(false);
                }}
                className="rounded-full border border-border/60 px-3 py-1 text-xs"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <>
            {comment.body && (
              <p className="text-[14px] leading-relaxed text-foreground/90"><Linkify text={comment.body} /></p>
            )}
            {(comment as any).media_url && (
              <img
                src={(comment as any).media_url}
                alt=""
                loading="lazy"
                className="mt-1 max-h-56 rounded-lg"
              />
            )}
          </>
        )}
      </div>
    </li>
  );
}

